import React, { FC, useCallback, useEffect, useState } from 'react';

import {
  List,
  TextField,
  ListProps,
  useListContext,
  DateField,
  useDataProvider,
  FunctionField,
  useNotify,
  useUnselectAll,
  InjectedFieldProps,
  PublicFieldProps,
} from 'react-admin';
import { makeStyles } from '@material-ui/core';
import uniqBy from 'lodash/uniqBy';
import { DateTime } from 'luxon';
import {
  BumpResourceSheetInterface,
  BumpResourceSheetType,
  ContractorPaymentTypeEnum,
  JobPhaseBumpApprovalStatus,
  RepairBumperPaymentTypeEnum,
  RepairFormulaInterface,
} from '@vatos-pas/common';
import Datagrid from 'components/Datagrid';
import { PermissionsProvider } from 'providers/permissionsProvider';

import {
  CAN_APPROVE_BUMPOUT,
  CAN_EDIT_BUMPOUT_TIMESHEET_PAID,
  CAN_MANAGE_HO_SERVICE_REPAIR,
  CAN_SEE_CONTRACTORS_BUMPOUT,
  CAN_SEE_BUMPOUT_TIMESHEET_NOTES,
  CAN_VIEW_BUMPOUT_TIMESHEET_PAID,
} from 'providers/permissions';
import { AppRole } from 'providers/roles';
import LogButton from '../components/LogButton';
import ApproveButton from '../components/ApproveButton';
import ContractorSelect from '../components/ContractorSelect';
import WorkedHoursField from '../components/WorkedHoursField';
import { JOB_PHASE_BUMP_APPROVAL_STATUS_LABEL_MAPPING } from '../constants';
import BumpoutTimesheetFilter from '../components/BumpoutTimesheetFilter';
import BulkActionButtons from '../components/BulkActionButtons';
import ListActions from '../components/ListActions';
import RepairTypeField from '../components/RepairTypeField';
import {
  findRepairFormula,
  getContractorFilter,
  isJobPhaseBumpType,
} from '../utils';
import {
  BumpoutSelectionRow,
  BumpoutSelectionRowsById,
  isRowApprovableBy,
  mergeSelectionRows,
  SELECTION_ROW_FIELDS,
} from '../selection';
import NotesButton from '../components/NotesButton';
import AddressField from '../components/AddressField';
import PaidCheckboxField from '../components/PaidCheckboxField';
import PhotoUpload from '../components/PhotoUpload';
import { PayoutAmount } from 'components/PayoutAmount';
import PhotosButton from '../components/PhotosButton';
import { UIProvider } from 'providers/uiProvider';
import { exporter } from 'providers/export';
import isMobileHook from 'hooks/isMobile';
import { trackEvent } from 'utils/mixpanel';
import { SupervisorField } from 'components/SupervisorListItems';
import { LocationStatusIcon } from 'components/LocationDetail';
import { MapProvider } from 'components/Map';

type BumpoutLocationFieldProps = PublicFieldProps &
  InjectedFieldProps<BumpResourceSheetInterface> & {
    variant: 'logged' | 'approved';
  };

const BumpoutLocationField: FC<BumpoutLocationFieldProps> = ({
  record,
  variant,
}) => {
  if (!record) return null;
  const isLogged = variant === 'logged';

  return (
    <LocationStatusIcon
      variant={variant}
      timestamp={isLogged ? record.loggedTimestamp : record.approvedTimestamp}
      geo={isLogged ? record.loggedGeo : record.approvedGeo}
      status={isLogged ? record.loggedGeoStatus : record.approvedGeoStatus}
      aggregateStatus={record.geoMatchStatus}
      distance={
        isLogged
          ? record.loggedGeoCalculatedDistance
          : record.approvedGeoCalculatedDistance
      }
      userName={isLogged ? record.loggedByUserName : record.approvedByUserName}
      address={record.fullAddress}
      jobGeo={record.jobGeo}
    />
  );
};

export type BumpoutTimesheetFields = {
  [key: string]: {
    [key: string]: DateTime | string | null;
  };
};

export enum BumpoutTimesheetFieldType {
  TimeIn = 'timeIn',
  TimeOut = 'timeOut',
  WorkedHours = 'workedHours',
}

type ListWrapperProps = {
  fields: BumpoutTimesheetFields | null;
  onChangeField: (
    id: string,
    name: BumpoutTimesheetFieldType,
    value: DateTime | string | null,
  ) => void;
  /** Remembers rows (page data, Select all) for the selection bar. */
  onRowsSeen: (rows: BumpoutSelectionRow[]) => void;
};

const ListWrapper = ({ onRowsSeen, ...props }: ListWrapperProps) => {
  const { data, refetch, loaded } =
    useListContext<BumpResourceSheetInterface>();

  const notify = useNotify();
  const dataProvider = useDataProvider();
  const { hasRole, hasPermission } = PermissionsProvider.useContainer();

  const canApprove = hasPermission(CAN_APPROVE_BUMPOUT);
  const canManageHOService = hasPermission(CAN_MANAGE_HO_SERVICE_REPAIR);
  const canSeeNotes = hasPermission(CAN_SEE_BUMPOUT_TIMESHEET_NOTES);
  const isSupervisor = hasRole(AppRole.Supervisor);
  const isBumper = hasRole(AppRole.Bumper);
  const isSuperAdmin = hasRole(AppRole.SuperAdmin);
  const canViewBumpoutPaid = hasPermission(CAN_VIEW_BUMPOUT_TIMESHEET_PAID);
  const canMarkPaid = hasPermission(CAN_EDIT_BUMPOUT_TIMESHEET_PAID);

  // Each region can only have its own contractors
  // We need a dictionary by regionId key
  // that will hold every contractor for that region
  const [contractors, setContractors] = useState<any>();
  const [isPhotoUploadOpen, setIsPhotoUploadOpen] = useState(false);
  const [loadingContractors, setLoadingContractors] = useState(false);

  const dataValues = Object.values(data);

  useEffect(() => {
    onRowsSeen(Object.values(data));
  }, [data, onRowsSeen]);

  const mappedData = dataValues.map(item => ({
    regionIdAndRepairId: `${item.regionId}-${item.repairId}`,
    regionId: item.regionId,
    repairId: item.repairId,
    bumpResourceType: item.bumpResourceType,
  }));
  const regionIds = uniqBy(mappedData, 'regionIdAndRepairId');

  const getFormulas = async () => {
    const response = await dataProvider.getList<RepairFormulaInterface>(
      'repair-formula',
      {
        filter: { active: true },
        pagination: { page: 1, perPage: 100 },
        sort: { field: 'createdAt', order: 'ASC' },
      },
    );

    return response?.data;
  };

  const getContractors = async (regionId: string, repairId: string) => {
    const repairFormulas = await getFormulas();
    const selectedRegion = regionIds.find(
      item => item.regionId === regionId && item.repairId === repairId,
    );
    if (selectedRegion) {
      const { regionIdAndRepairId, regionId, repairId, bumpResourceType } =
        selectedRegion;
      setLoadingContractors(true);
      try {
        const response = await dataProvider.getList('contractor', {
          filter: getContractorFilter(
            repairFormulas,
            repairId,
            regionId,
            bumpResourceType,
          ),
          pagination: { page: 1, perPage: 200 },
          sort: { field: 'name', order: 'ASC' },
        });

        if (response?.data?.length) {
          let contractors = [...response.data];

          const isRepairPayment =
            bumpResourceType === BumpResourceSheetType.REPAIR_PAYMENT;

          if (isRepairPayment) {
            const perBoard = findRepairFormula(
              repairFormulas,
              repairId,
              RepairBumperPaymentTypeEnum.PerBoard,
            );
            const perHour = findRepairFormula(
              repairFormulas,
              repairId,
              RepairBumperPaymentTypeEnum.PerHour,
            );

            const hasPerBoard = perBoard !== undefined;
            const hasPerHour = perHour !== undefined;

            if (hasPerBoard && !hasPerHour) {
              contractors = contractors?.filter(
                contractor =>
                  contractor.bumperPaymentType ===
                  ContractorPaymentTypeEnum.PerBoard,
              );
            }
            if (hasPerHour && !hasPerBoard) {
              contractors = contractors?.filter(
                contractor =>
                  contractor.bumperPaymentType ===
                  ContractorPaymentTypeEnum.PerHour,
              );
            }
          }

          setContractors(prevState => ({
            ...prevState,
            [regionIdAndRepairId]: contractors,
          }));
        }
      } catch (error: any) {
        notify(error.message, 'warning');
      } finally {
        setLoadingContractors(false);
      }
    }
  };

  return (
    <Datagrid<BumpoutSelectionRow>
      selectAllRows={
        canMarkPaid && {
          extraFields: SELECTION_ROW_FIELDS,
          onRowsFetched: onRowsSeen,
        }
      }
      isRowSelectable={record =>
        canMarkPaid ||
        isRowApprovableBy(record, { canApprove, canManageHOService })
      }
      {...props}
    >
      <RepairTypeField label="Repair Type" />
      <TextField source="jobLot" label="Lot" />
      <TextField source="jobBlock" label="Block" />
      <TextField source="builderName" label="Builder" />
      <TextField source="subdivisionName" label="Subdivision" />
      <AddressField label="Address" />
      {hasPermission(CAN_SEE_CONTRACTORS_BUMPOUT) && (
        <ContractorSelect
          label="Contractor"
          contractors={contractors}
          getContractors={getContractors}
          loadingContractors={loadingContractors}
        />
      )}
      {!isSupervisor && <TextField source="createdByName" label="Created By" />}
      <SupervisorField label="Supervisors" />
      <DateField source="repairDate" label="Repair Date" />
      <TextField source="payableBoardsTotal" label="Payable Boards" />
      {canSeeNotes && <NotesButton label="Notes" />}
      <PhotosButton label="Photos" />
      <LogButton
        loaded={loaded}
        onChangeField={props.onChangeField}
        handleRefetch={refetch}
      />
      <WorkedHoursField
        label="Worked Hours"
        loaded={loaded}
        value={BumpoutTimesheetFieldType.WorkedHours}
        onChangeField={props.onChangeField}
        handleRefetch={refetch}
      />
      <TextField source="estimatedHours" label="Estimated Hours" />
      <FunctionField
        label="Payout"
        render={record =>
          isJobPhaseBumpType(record?.bumpResourceType) ? (
            '-'
          ) : (
            <PayoutAmount record={record} />
          )
        }
      />
      {isSuperAdmin && (
        <BumpoutLocationField variant="logged" label="Log Location" />
      )}
      <FunctionField
        label="Status"
        render={record =>
          JOB_PHASE_BUMP_APPROVAL_STATUS_LABEL_MAPPING[
            record.bumperApprovalStatus
          ]
        }
      />
      {canViewBumpoutPaid && <PaidCheckboxField label="Paid?" />}
      {!isBumper && (
        <FunctionField
          label="Approval Timestamp"
          source="approvedTimestamp"
          render={record => {
            const timestamp = record.approvedTimestamp;

            if (!timestamp && !isSuperAdmin) return null;

            return (
              <span
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
              >
                {timestamp &&
                  DateTime.fromISO(timestamp).toFormat('MM/dd/yyyy - t')}
                {isSuperAdmin && (
                  <BumpoutLocationField variant="approved" record={record} />
                )}
              </span>
            );
          }}
        />
      )}
      <ApproveButton handleRefetch={refetch} fields={props.fields} />
      <PhotoUpload
        open={isPhotoUploadOpen}
        handleClose={() => setIsPhotoUploadOpen(false)}
      />
    </Datagrid>
  );
};

const BumpoutTimesheetListInner: FC<ListProps> = props => {
  const { hasRole, hasPermission } = PermissionsProvider.useContainer();
  const classes = useStyles();
  const { opened } = UIProvider.useContainer();
  const isMobile = isMobileHook();

  const [fields, setFields] = useState<BumpoutTimesheetFields | null>(null);

  const onChangeField = (
    id: string,
    name: BumpoutTimesheetFieldType,
    value: DateTime | string | null,
  ) =>
    setFields(prevState => ({
      ...prevState,
      [id]: {
        ...prevState?.[id],
        [name]: value,
      },
    }));

  // Rows seen across pages and Select all, so the bar can judge a selection
  // that spans pages and split it by row type.
  const [selectionRows, setSelectionRows] = useState<BumpoutSelectionRowsById>(
    {},
  );
  const onRowsSeen = useCallback(
    (rows: BumpoutSelectionRow[]) =>
      setSelectionRows(prevState => mergeSelectionRows(prevState, rows)),
    [],
  );

  // The store keeps a selection after the list unmounts; rows chosen on an
  // earlier visit must not be approved or paid from this one.
  const unselectAll = useUnselectAll(props.resource);
  useEffect(() => {
    unselectAll();
  }, [unselectAll]);

  const isSupervisor = hasRole(AppRole.Supervisor);
  const isBumper = hasRole(AppRole.Bumper);
  const isRegionalManager = hasRole(AppRole.RegionalManager);
  const hasSelectionBar =
    hasPermission(CAN_APPROVE_BUMPOUT) ||
    hasPermission(CAN_EDIT_BUMPOUT_TIMESHEET_PAID);
  useEffect(() => {
    trackEvent('PageView', {
      resource: 'bumpout-timesheet',
      page: window.location.pathname,
      role: localStorage.getItem('userRoles'),
      env: process.env.REACT_APP_MIXPANEL_ENV,
    });
  }, []);
  return (
    <List
      classes={{
        content: classes.content,
        root: !isMobile
          ? opened
            ? classes.root
            : classes.rootClosed
          : classes.rootMobile,
      }}
      {...props}
      empty={false}
      filters={<BumpoutTimesheetFilter>{props.filters}</BumpoutTimesheetFilter>}
      filterDefaultValues={{
        ...((isSupervisor || isRegionalManager) && {
          'bumperApprovalStatus||$in': [
            JobPhaseBumpApprovalStatus.PENDING_REVIEW,
            JobPhaseBumpApprovalStatus.PENDING_APPROVAL,
          ],
        }),
        ...(isBumper && {
          'bumperApprovalStatus||$in': [JobPhaseBumpApprovalStatus.NOT_STARTED],
        }),
      }}
      sort={{ field: 'repairDate', order: 'DESC' }}
      bulkActionButtons={
        hasSelectionBar && (
          <BulkActionButtons fields={fields} selectionRows={selectionRows} />
        )
      }
      exporter={exporter('bumpout-timesheet')}
      actions={<ListActions maxResults={1000} />}
      perPage={5}
    >
      <ListWrapper
        fields={fields}
        onChangeField={onChangeField}
        onRowsSeen={onRowsSeen}
      />
    </List>
  );
};

export const BumpoutTimesheetList: FC<ListProps> = props => (
  <MapProvider>
    <BumpoutTimesheetListInner {...props} />
  </MapProvider>
);

const useStyles = makeStyles({
  content: {
    overflow: 'auto',
  },
  root: {
    width: 'calc(100vw - 290px)',
  },
  rootClosed: {
    width: 'calc(100vw - 135px)',
  },
  rootMobile: {
    width: 'calc(100vw - 24px)',
  },
});
